import { stringifyProps, tryParseProps } from '@gotti/core';
import type { AuthenticatedGateClient } from '../servers/GateServer.js';
import { ENCODED_AUTH_PROP_KEYS, typeMarkerFieldFor, typeMarkerValue, writeTypeMarkers, restoreTypedAuthProps, restoreNumericAuthProps, normalizeStringAuthProps, normalizeStringAuthValue } from './authRecordEncoding.js';
import type { RedisClientWrapper } from '../RedisClientWrapper.js';
import type { GateStateStore, GateStateStoreOptions } from './GateStateStore.js';

const DEFAULT_KEY_PREFIX = 'gotti:';

/**
 * Expiry refreshed onto every per-client index key this store writes —
 * `areas:{privateId}` on `addClientArea`, `conn:{connectorId}` on
 * `addClientToConnector`, `reserving:{privateId}` on `setReservingSeat`. A
 * crash-recovery backstop, NOT a lifecycle mechanism (see `addClientArea`).
 * Seven days: far beyond any session that could still be legitimately holding
 * the rows, far short of forever.
 *
 * SESSION-scale, deliberately, not grace-window-scale: a row's legitimate life
 * is its session's. A player sitting in one area for hours emits no further
 * report and reserves no further seat, and their index must still be accurate
 * the moment they drop — a short TTL would silently delete a LIVE player's
 * state and downgrade their reconnect to a fresh join, which is strictly worse
 * than the leak it would be bounding.
 *
 * (The grace HOLD key is the one exception: it is inherently window-scaled, so
 * `setGraceHold` sizes its own expiry from the caller's `ttlMs`.)
 */
const CLIENT_INDEX_TTL_SECONDS = 60 * 60 * 24 * 7;

/**
 * Redis-backed `GateStateStore` — ports GateServer's ten `if (this.redis)`
 * forks verbatim in behaviour (same `p:${privateId}` / `pubIdMap` /
 * `identityMap` keys, same `stringifyProps`/`tryParseProps` encoding via
 * `ENCODED_AUTH_PROP_KEYS`), with every key prefixed by `keyPrefix`
 * (default `'gotti:'`).
 *
 * Structural fix (the C1 class): `setAuthRecord`/`updateAuthField` skip any
 * field whose value is `undefined` before writing. node-redis' RESP2
 * encoder throws `TypeError: Invalid argument type` on an `undefined` field
 * value (see `FakeRedisClient`'s `coerce`) — today's `setUserAuth` writes
 * `stringifyProps({...data}, ENCODED_AUTH_PROP_KEYS)` straight into `hset`
 * with no such guard, so any optional field left `undefined` on the record
 * (`areas`, `gameId`, `connectorId`, `identityKey`, ...) crashes a
 * redis-backed gate. The conformance suite's "record with UNDEFINED
 * optional fields" test pins this.
 */
export class RedisGateStateStore implements GateStateStore {
    private readonly prefix: string;

    constructor(private readonly redis: RedisClientWrapper, opts: GateStateStoreOptions = {}) {
        this.prefix = opts.keyPrefix ?? DEFAULT_KEY_PREFIX;
    }

    private authKey(privateId: string): string {
        return `${this.prefix}p:${privateId}`;
    }

    private pubIdMapKey(): string {
        return `${this.prefix}pubIdMap`;
    }

    private identityMapKey(): string {
        return `${this.prefix}identityMap`;
    }

    private areasKey(privateId: string): string {
        return `${this.prefix}areas:${privateId}`;
    }

    private holdKey(privateId: string): string {
        return `${this.prefix}hold:${privateId}`;
    }

    private seqIdsKey(): string {
        return `${this.prefix}seqIds`;
    }

    private seqCounterKey(): string {
        return `${this.prefix}seqCounter`;
    }

    private reservingKey(privateId: string): string {
        return `${this.prefix}reserving:${privateId}`;
    }

    private connKey(connectorId: string | number): string {
        return `${this.prefix}conn:${connectorId}`;
    }

    async getAuthRecord(privateId: string): Promise<AuthenticatedGateClient<any> | undefined> {
        const client: any = await this.redis.hgetall(this.authKey(privateId));
        if (!client?.privateId) return undefined;
        tryParseProps(client, ENCODED_AUTH_PROP_KEYS);
        // A hash stores only strings, so a NUMERIC `publicId`/`gameId` would
        // otherwise come back as one — a game-visible type change between
        // backings, and (before the seat comparison was normalized) a join that
        // could never complete. Strips the marker fields too, so the gate gets
        // back the record shape it wrote and `MemoryGateStateStore` hands back
        // the identical object.
        restoreTypedAuthProps(client);
        // Same contract, no marker needed: `time` and `heldGraceMs` are declared
        // plain `number`, and this record goes straight to game code via
        // `onJoinGame`. See `NUMERIC_AUTH_PROPS`.
        //
        // ORDER-INDEPENDENT of the line above: the two touch disjoint fields and
        // neither reads what the other writes (see `NUMERIC_AUTH_PROPS`).
        restoreNumericAuthProps(client);
        return client;
    }

    async setAuthRecord(privateId: string, record: AuthenticatedGateClient<any>): Promise<void> {
        // Applied on the COPY, so this store never mutates the caller's record
        // (the memory store owns that object and normalizes it there). A hash
        // would have stringified `connectorId` anyway — the call is here so the
        // ONE list in `authRecordEncoding` governs both backings and a prop
        // added to it can never be normalized on one and not the other.
        const encoded: any = normalizeStringAuthProps(stringifyProps({ ...record }, ENCODED_AUTH_PROP_KEYS));
        const toWrite: Record<string, any> = {};
        for (const key of Object.keys(encoded)) {
            // Skip undefined-valued fields before writing — see class doc.
            if (encoded[key] === undefined) continue;
            toWrite[key] = encoded[key];
        }
        // Written UNCONDITIONALLY beside every marked id (not "only when
        // numeric"), so a record whose `publicId`/`gameId` changes type can
        // never keep a stale marker — see `TYPE_MARKED_AUTH_PROPS`. Skipped only
        // for a prop that is not being written at all, which is the same
        // condition that skipped the field itself.
        writeTypeMarkers(toWrite, record);
        await this.redis.hset(this.authKey(privateId), toWrite);
    }

    async updateAuthField(privateId: string, key: string, value: any): Promise<void> {
        if (!(await this.getAuthRecord(privateId))) return;
        // Skip undefined-valued fields before writing — see class doc.
        if (value === undefined) return;
        if (key === 'publicId') {
            // Ports updateUserAuth's redis branch verbatim: changing the
            // publicId field also has to move the pubIdMap pointer, or the
            // old publicId would keep resolving to this privateId forever.
            // No INTERNAL GateServer call site passes key === 'publicId'
            // today (reserveSeat/consumeRejoinAreas/applyClientAreaChange/
            // claimIdentity/the games-list handler all pass other keys) —
            // but `updateUserAuth` is called directly with this key from
            // apps/integration-tests/.../ClientToGate.test.ts:80-99, so this
            // branch is live and must stay correct on both backings; see
            // `MemoryGateStateStore.updateAuthField` for the memory-side
            // mirror this was missing until that test's sequence was traced
            // through the store (task-2-report.md, Finding 1).
            const oldId = await this.redis.hget(this.authKey(privateId), 'publicId');
            // GUARDED, mirroring `MemoryGateStateStore`'s `oldId !== undefined
            // && oldId !== null` — a record with no publicId field reads back
            // `null` here, and the wrapper now normalizes every field, so an
            // unguarded call would `HDEL` a field literally named `'null'`
            // instead of throwing. Harmless either way, but the two backings
            // must not differ on which fields they touch.
            if (oldId != null) {
                await this.redis.hdel(this.pubIdMapKey(), oldId as any);
            }
            await this.redis.hset(this.pubIdMapKey(), value, privateId);
            // (The pubIdMap above stays string-KEYED regardless:
            // `RedisClientWrapper` `String()`s every field it passes down, which
            // is what `getPublicIdOwner`/`deletePublicIdOwner`'s own `String()`
            // coercion lines up with. The map's key and the record's field are
            // different things, and only the field carries a type.)
        }
        // The OTHER writer of a marked field, and it must keep the marker in
        // step with it — see `setAuthRecord`. It is also the PRIMARY writer on
        // the live join path for `gameId`: `reserveSeat` persists the joined
        // game with `updateUserAuth(privateId, 'gameId', gameId)` and never
        // touches `setAuthRecord`, so a marker written only there would never
        // fire for it. Marked props are never `ENCODED_AUTH_PROP_KEYS`, so the
        // two branches below are mutually exclusive by construction.
        const markerField = typeMarkerFieldFor(key);
        if (markerField) {
            await this.redis.hset(this.authKey(privateId), markerField, typeMarkerValue(value));
        } else if (ENCODED_AUTH_PROP_KEYS.includes(key)) {
            value = typeof value === 'string' ? value : JSON.stringify(value);
        }
        // Mirror of `setAuthRecord`'s normalization, on the update path — see
        // `MemoryGateStateStore.updateAuthField` for why this writer, not just
        // `setAuthRecord`, has to apply it. A no-op on the wire here (the hash
        // stringifies regardless); present so the two backings run the same
        // rule from the same list.
        value = normalizeStringAuthValue(key, value);
        await this.redis.hset(this.authKey(privateId), key, value);
    }

    async deleteAuthRecord(privateId: string): Promise<void> {
        await this.redis.del(this.authKey(privateId));
    }

    async getPublicIdOwner(publicId: string | number): Promise<string | undefined> {
        const owner = await this.redis.hget(this.pubIdMapKey(), publicId);
        return owner == null ? undefined : String(owner);
    }

    async setPublicIdOwner(publicId: string | number, privateId: string): Promise<void> {
        await this.redis.hset(this.pubIdMapKey(), publicId, privateId);
    }

    async deletePublicIdOwner(publicId: string | number): Promise<void> {
        await this.redis.hdel(this.pubIdMapKey(), publicId);
    }

    async getIdentityOwner(identityKey: string): Promise<string | undefined> {
        const owner = await this.redis.hget(this.identityMapKey(), identityKey);
        return owner == null ? undefined : String(owner);
    }

    async setIdentityOwner(identityKey: string, privateId: string): Promise<void> {
        await this.redis.hset(this.identityMapKey(), identityKey, privateId);
    }

    async clearIdentityOwner(identityKey: string): Promise<void> {
        await this.redis.hdel(this.identityMapKey(), identityKey);
    }

    // Client→area index: a direct per-field HSET/HDEL, never a read (hgetall)
    // + mutate + write-back — see the interface doc for why: two calls for
    // the SAME privateId but different areas, fired without an intervening
    // await, must BOTH land, and a read-modify-write would let the second
    // write silently lose the first under real cross-process interleaving.
    async addClientArea(privateId: string, areaId: string, graceMs: number): Promise<void> {
        await this.redis.hset(this.areasKey(privateId), areaId, String(graceMs));
        // CRASH BACKSTOP ONLY — refreshed on every write, exactly the shape
        // `setGraceHold` uses. `GateServer.applyClientAreaChange` already deletes
        // a row whose record was purged mid-report (its compensating delete), and
        // `removeUserAuth` deletes the whole key on an ordinary purge; this covers
        // the one case neither can — a gate that DIES between writing the row and
        // checking the record, leaving a key no live process knows about.
        //
        // NOT sized off the grace window, deliberately — see the constant.
        //
        // RESIDUAL, named rather than hidden: unlike `SET`, `HSET` has no
        // inline-expiry option (and per-FIELD TTLs — `HEXPIRE` — arrived only in
        // redis 7.4, which this store does not require), so this really is two
        // commands and a gate that dies exactly between them leaves the key with
        // no expiry. Bounded in practice by the fact that the NEXT write for the
        // same client re-applies it (`HSET` does NOT clear a key's TTL, so the
        // refresh is free), and a client that never writes again is by
        // definition one whose gate died mid-report — a single orphan hash per
        // such crash, not a per-session leak. The same shape applies to
        // `addClientToConnector`'s `SADD` + `EXPIRE`, which additionally
        // self-heals: any OTHER client seated on that connector re-expires the
        // shared key.
        await this.redis.expire(this.areasKey(privateId), CLIENT_INDEX_TTL_SECONDS);
    }

    async removeClientArea(privateId: string, areaId: string): Promise<void> {
        await this.redis.hdel(this.areasKey(privateId), areaId);
    }

    async getClientAreas(privateId: string): Promise<Array<{ areaId: string, graceMs: number }>> {
        const hash = await this.redis.hgetall(this.areasKey(privateId));
        if (!hash) return [];
        return Object.keys(hash).map((areaId) => ({ areaId, graceMs: Number(hash[areaId]) }));
    }

    async clearClientAreas(privateId: string): Promise<void> {
        await this.redis.del(this.areasKey(privateId));
    }

    async setGraceHold(privateId: string, connectorId: string | number, ttlMs: number): Promise<void> {
        // ONE COMMAND (`SET key value EX n`), not `SET` + `EXPIRE`. Backstop
        // only — crash-recovery slack if the owning gate dies mid-hold; the
        // owning gate's own timer clears the hold well before this fires on the
        // happy path. But the crash this bounds is precisely a gate dying at an
        // arbitrary instant, and with two commands one of those instants is
        // BETWEEN them: the hold would then exist with no expiry at all and
        // nothing left alive to clear it — the backstop skipped by the very
        // failure it exists for. Atomic set-with-expiry removes that instant.
        await this.redis.set(this.holdKey(privateId), String(connectorId), { EX: Math.ceil(ttlMs / 1000) + 30 });
    }

    async getGraceHold(privateId: string): Promise<{ connectorId: string | number } | undefined> {
        const value = await this.redis.get(this.holdKey(privateId));
        return value == null ? undefined : { connectorId: String(value) };
    }

    /**
     * ATOMIC CLAIM — see the interface. `DEL` returns the number of keys it
     * actually removed, and it is a single command, so of N gates racing the
     * same hold exactly one gets a non-zero count back. That is the whole
     * cross-gate synchronisation; nothing else is needed and nothing weaker
     * would do (a `GET` then `DEL` would let both sides read the same value).
     */
    async clearGraceHold(privateId: string): Promise<boolean> {
        return (await this.redis.del(this.holdKey(privateId))) > 0;
    }

    async releaseSequentialId(id: number): Promise<void> {
        await this.redis.rpush(this.seqIdsKey(), [id]);
    }

    async acquireSequentialId(): Promise<number | undefined> {
        const value = await this.redis.lpop(this.seqIdsKey());
        return value == null ? undefined : Number(value);
    }

    /**
     * `INCR` — one command, returns the NEW value, and redis creates the key at
     * 0 first, so the first mint is 1 (the same first id the per-process
     * counter used to hand out). Of N gates minting concurrently each gets its
     * own number; that is the entire cross-gate synchronisation, and nothing
     * weaker works (a GET then SET would let two gates read the same value —
     * which is precisely the collision this replaces).
     *
     * The counter key deliberately has NO TTL: it is a monotonic high-water
     * mark for the whole deployment, and resetting it would re-issue ids that
     * live sessions still hold.
     */
    async nextSequentialId(): Promise<number> {
        return this.redis.incr(this.seqCounterKey());
    }

    async setReservingSeat(privateId: string, connectorId: string | number): Promise<void> {
        // CRASH BACKSTOP, refreshed on every write — the same shape
        // `addClientArea` uses, for the same reason. `GateServer.removeSeat`
        // deletes this key on every settled reservation and `removeUserAuth`
        // deletes it on every purge; this covers only what neither can — a gate
        // that DIES between reserving a seat and settling it, leaving a key no
        // live process knows about.
        //
        // Written as ONE command (`SET key value EX n`) rather than `SET` then
        // `EXPIRE`: redis `SET` clears any existing expiry, so the TTL cannot
        // precede the write — and if it FOLLOWS the write, a gate that dies in
        // between leaves an un-TTL'd key, i.e. the crash case skips the crash
        // backstop. See `setGraceHold`.
        await this.redis.set(this.reservingKey(privateId), String(connectorId), { EX: CLIENT_INDEX_TTL_SECONDS });
    }

    async getReservingSeat(privateId: string): Promise<string | number | undefined> {
        const value = await this.redis.get(this.reservingKey(privateId));
        return value == null ? undefined : String(value);
    }

    async clearReservingSeat(privateId: string): Promise<void> {
        await this.redis.del(this.reservingKey(privateId));
    }

    async addClientToConnector(connectorId: string | number, privateId: string): Promise<void> {
        await this.redis.sadd(this.connKey(connectorId), [privateId]);
        // CRASH BACKSTOP, refreshed on every write — see `addClientArea`. The
        // set drains member-by-member on the ordinary paths (a purge, a re-seat,
        // a resume's re-key), and `handleConnectorClosed` drops any member whose
        // record is gone; this bounds the one case none of those reach — a gate
        // that dies owning the only knowledge that those members are stale. The
        // TTL is per-KEY, so an active connector's set is refreshed by every new
        // seat it is granted and can never expire under a live population.
        await this.redis.expire(this.connKey(connectorId), CLIENT_INDEX_TTL_SECONDS);
    }

    async removeClientFromConnector(connectorId: string | number, privateId: string): Promise<void> {
        await this.redis.srem(this.connKey(connectorId), [privateId]);
    }

    async getConnectorClients(connectorId: string | number): Promise<string[]> {
        const members = await this.redis.smembers(this.connKey(connectorId));
        return members.map((m) => String(m));
    }

    async clearConnectorClients(connectorId: string | number): Promise<void> {
        await this.redis.del(this.connKey(connectorId));
    }
}
